import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetchJson, fetchAllPages } from "../apiClient";
import usePagedList from "../hooks/usePagedList";
import "./AdminCollections.css";

const TRIGGERS_PATH = "/api/v1/agents/triggers";
const KINDS = ["schedule", "webhook", "event"];
const EVENTS = ["document.processed", "job.completed", "job.failed"];
const EMPTY = { subject: "", kind: "schedule", name: "", task: "", cron: "0 7 * * 1-5", timezone: "", event: EVENTS[0] };

/** Triggers: start an agent or workflow on a schedule, from a signed webhook, or when something
 * happens in the system. Runs always act as the trigger's owner. */
export default function TriggersTab() {
  const { t } = useTranslation();
  const { items, loading, error, hasMore, loadMore, reload } = usePagedList(TRIGGERS_PATH);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);       // {tone, text}
  const [secret, setSecret] = useState(null);         // {path, secret} — shown once after creation/rotation

  useEffect(() => {
    Promise.all([fetchAllPages("/api/v1/agents?scope=all"), fetchAllPages("/api/v1/workflows?scope=all")])
      .then(([agents, workflows]) => {
        const runnable = [
          ...agents.filter((a) => a.can_run).map((a) => ({ value: `agent:${a.id}`, label: a.name })),
          ...workflows.filter((w) => w.can_run).map((w) => ({ value: `workflow:${w.id}`, label: `${w.name} (${t("triggers.workflow")})` })),
        ];
        setSubjects(runnable);
        setForm((f) => ({ ...f, subject: f.subject || (runnable[0] || {}).value || "" }));
      })
      .catch((e) => setMessage({ tone: "danger", text: e.message }));
  }, [t]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const act = async (request, success) => {
    setBusy(true);
    try {
      const result = await request();
      setMessage(success ? { tone: "success", text: success } : null);
      await reload();
      return result;
    } catch (e) {
      setMessage({ tone: "danger", text: e.message });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    const [subjectKind, subjectId] = form.subject.split(":");
    const config = form.kind === "schedule" ? { cron: form.cron, ...(form.timezone ? { timezone: form.timezone } : {}) }
      : form.kind === "event" ? { event: form.event } : {};
    const created = await act(() => apiFetchJson(TRIGGERS_PATH, {
      method: "POST",
      body: JSON.stringify({ subject_kind: subjectKind, subject_id: subjectId, kind: form.kind, name: form.name,
                             task: form.task, config }),
    }), t("triggers.created"));
    if (created) {
      setForm((f) => ({ ...EMPTY, subject: f.subject }));
      if (created.secret) setSecret({ path: created.webhook_path, secret: created.secret });
    }
  };

  const rotate = async (trigger) => {
    const data = await act(() => apiFetchJson(`${TRIGGERS_PATH}/${trigger.id}/rotate-secret`, { method: "POST" }));
    if (data) setSecret({ path: trigger.webhook_path, secret: data.secret });
  };

  const subjectLabel = (trigger) =>
    (subjects.find((s) => s.value === `${trigger.subject_kind}:${trigger.subject_id}`) || {}).label || trigger.subject_id;

  return (
    <div className="ds-page">
      <header className="ds-page-header">
        <h2 className="ds-page-title">{t("triggers.title")}</h2>
      </header>
      {(message || error) && (
        <div className={`ds-alert ds-alert--${message ? message.tone : "danger"}`} role="alert">
          {message ? message.text : error}
        </div>
      )}
      {secret && (
        <div className="ds-alert ds-alert--warning" role="status">
          <p>{t("triggers.secretOnce")}</p>
          <p className="ds-mono">{secret.path}</p>
          <p className="ds-mono" data-testid="trigger-secret">{secret.secret}</p>
          <p className="ds-help">{t("triggers.signatureHelp")}</p>
          <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" onClick={() => setSecret(null)}>
            {t("triggers.secretSaved")}
          </button>
        </div>
      )}
      <form className="ds-card ac-form-stack" onSubmit={create} aria-label={t("triggers.create")}>
        <div className="ds-form-row">
          <div className="ds-field">
            <label className="ds-label" htmlFor="trigger-subject">{t("triggers.subject")}</label>
            <select id="trigger-subject" className="ds-select" value={form.subject} onChange={set("subject")}>
              {subjects.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="ds-field">
            <label className="ds-label" htmlFor="trigger-kind">{t("triggers.kind")}</label>
            <select id="trigger-kind" className="ds-select" value={form.kind} onChange={set("kind")}>
              {KINDS.map((k) => <option key={k} value={k}>{t(`triggers.kind_${k}`)}</option>)}
            </select>
          </div>
          <div className="ds-field">
            <label className="ds-label" htmlFor="trigger-name">{t("triggers.name")}</label>
            <input id="trigger-name" className="ds-input" value={form.name} onChange={set("name")} />
          </div>
        </div>
        {form.kind === "schedule" && (
          <div className="ds-form-row">
            <div className="ds-field">
              <label className="ds-label" htmlFor="trigger-cron">{t("triggers.cron")}</label>
              <input id="trigger-cron" className="ds-input ds-mono" value={form.cron} onChange={set("cron")} />
              <span className="ds-help">{t("triggers.cronHelp")}</span>
            </div>
            <div className="ds-field">
              <label className="ds-label" htmlFor="trigger-timezone">{t("triggers.timezone")}</label>
              <input id="trigger-timezone" className="ds-input" placeholder="Europe/Berlin" value={form.timezone}
                     onChange={set("timezone")} />
            </div>
          </div>
        )}
        {form.kind === "event" && (
          <div className="ds-field">
            <label className="ds-label" htmlFor="trigger-event">{t("triggers.event")}</label>
            <select id="trigger-event" className="ds-select" value={form.event} onChange={set("event")}>
              {EVENTS.map((ev) => <option key={ev} value={ev}>{t(`triggers.event_${ev.replace(".", "_")}`)}</option>)}
            </select>
          </div>
        )}
        <div className="ds-field">
          <label className="ds-label" htmlFor="trigger-task">{t("triggers.task")}</label>
          <textarea id="trigger-task" className="ds-textarea" rows={2} value={form.task} onChange={set("task")} />
          <span className="ds-help">{t("triggers.taskHelp")}</span>
        </div>
        <div className="ac-actions">
          <button type="submit" className="ds-btn ds-btn--primary" disabled={busy || !form.subject || !form.task.trim()}>
            {t("triggers.create")}
          </button>
        </div>
      </form>

      {!loading && items.length === 0 ? <div className="ds-empty">{t("triggers.none")}</div> : (
        <ul className="ds-list">
          {items.map((trigger) => (
            <li key={trigger.id} className="ds-list-row">
              <div className="ds-list-detail">
                <p className="ds-list-title">
                  {trigger.name || subjectLabel(trigger)} · {t(`triggers.kind_${trigger.kind}`)}
                  {!trigger.enabled && <span className="ds-badge ds-badge--warning"> {t("triggers.disabled")}</span>}
                </p>
                <p className="ds-meta">
                  {subjectLabel(trigger)}
                  {trigger.kind === "schedule" && ` · ${trigger.config.cron} (${trigger.config.timezone})`}
                  {trigger.kind === "event" && ` · ${trigger.config.event}`}
                  {trigger.kind === "webhook" && ` · ${trigger.webhook_path}`}
                </p>
                {trigger.next_fire_at && <p className="ds-meta">{t("triggers.next", { at: trigger.next_fire_at })}</p>}
                {trigger.last_error && <div className="ds-alert ds-alert--danger">{trigger.last_error}</div>}
                <div className="ac-actions">
                  <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={busy}
                          onClick={() => act(() => apiFetchJson(`${TRIGGERS_PATH}/${trigger.id}`, {
                            method: "PATCH", body: JSON.stringify({ enabled: !trigger.enabled }) }))}>
                    {t(trigger.enabled ? "triggers.disable" : "triggers.enable")}
                  </button>
                  <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={busy}
                          onClick={() => act(() => apiFetchJson(`${TRIGGERS_PATH}/${trigger.id}/fire`, { method: "POST" }),
                                             t("triggers.fired"))}>
                    {t("triggers.fireNow")}
                  </button>
                  {trigger.kind === "webhook" && (
                    <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={busy}
                            onClick={() => rotate(trigger)}>{t("triggers.rotate")}</button>
                  )}
                  <button type="button" className="ds-btn ds-btn--danger ds-btn--sm" disabled={busy}
                          onClick={() => act(() => apiFetchJson(`${TRIGGERS_PATH}/${trigger.id}`, { method: "DELETE" }))}>
                    {t("triggers.delete")}
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {hasMore && (
        <div className="ac-actions">
          <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={loading} onClick={loadMore}>
            {t("agents.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
}
